/* eslint-disable */
import React, { useContext, useState, useEffect, useCallback, useMemo, memo } from 'react';
import PropTypes from 'prop-types';
import styled from 'styled-components';

import {
  useEditor,
  useEditorState,
  EditorContent,
  BubbleMenu,
} from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import TextStyle from '@tiptap/extension-text-style';
import { Color } from '@tiptap/extension-color';
import Underline from '@tiptap/extension-underline';
import {
  faBold,
  faItalic,
  faStrikethrough,
  faHeading,
  faRemoveFormat,
  faHighlighter,
  faUnderline,
} from '@fortawesome/free-solid-svg-icons';
import { HexAlphaColorPicker } from 'react-colorful';
import ThemeContext from '../../../../theme/ThemeContext';
import Icon from '../../../misc/Icon';

const inputStyles = (props) => {
  return `
    overflow-y: auto;
    height: 100%;
    background-color: ${props.backgroundColor}!important;
    border-radius: ${props.borderRadius}px;
    border: ${props.borderWidth}px solid ${props.borderColor};
    padding: ${props.padding}px;
    resize: none;
    transition: border 0.15s ease-in-out;
    font-size:${props.fontSize}px !important;
    font-style: ${props.fontStyle};
    font-weight: ${props.fontWeight};
    text-align: ${props.textAlign};
    text-transform: ${props.textTransform};
    text-decoration: ${props.textDecoration};
    ${props.textOverflow ? `text-overflow: ${props.textOverflow};` : ''}
    line-height: ${props.lineHeightPixels !== undefined ? `${props.lineHeightPixels}px` : 'initial'} !important;
    background-color: transparent;


    &:focus, &:focus-visible {
      outline: unset;
      border: ${props.borderWidth}px solid ${props.theme.primaryColor};
    }

    &:hover {
      ${props.disabled ? '' : `border: ${props.borderWidth}px solid ${props.theme.primaryColor}`};
    }
  `;
};

const HeadingDropdown = styled.div`
  position: absolute;
  top: 34px;
  left: 39%;
  background-color: ${(props) => props.theme.backgroundColor || '#fff'};
  border: 1px solid ${(props) => props.theme.borderColor || '#e0e0e0'};
  border-radius: 6px;
  padding: 8px;
  z-index: 20000;
  display: flex;
  flex-direction: column;
  gap: 4px;

  button {
    display: flex;
    align-items: center;
    padding: 4px 6px;
    border: none;
    background: transparent;
    border-radius: 4px;

    &:hover {
      background-color: ${(props) => props.theme.secondaryColor || '#f7f7f7'};
      cursor: pointer;
    }
    &.is-active {
      background-color: ${(props) => props.theme.primaryColor || '#dcdcdc'};
      color: ${(props) => props.theme.backgroundColor || '#333'};
    }
  }
`;

const ColorPickerPopover = styled.div`
  position: absolute;
  top: 34px;
  right: 0;
  background-color: ${(props) => props.theme.backgroundColor || '#fff'};
  border: 1px solid ${(props) => props.theme.borderColor || '#e0e0e0'};
  border-radius: 6px;
  padding: 8px;
  z-index: 20000;

  .react-colorful {
    width: 140px;
    height: 120px;
  }
  .react-colorful__pointer {
    width: 20px !important;
    height: 20px !important;
  }
`;

const CompactTiptapInput = styled.div`
  height: 100%;
  overflow-y: auto;
  .tiptap.ProseMirror {
    ${(props) => inputStyles(props)}
  }

  .tiptap.ProseMirror > *:first-child {
    margin-top: 4px !important;
  }
`;

const BubbleMenuContainer = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
  background-color: ${(props) => props.theme.backgroundColor || '#fff'};
  border: 1px solid ${(props) => props.theme.borderColor || '#e0e0e0'};
  border-radius: ${(props) => props.theme.borderRadius || 8}px;
  box-shadow: 0 2px 5px rgba(0, 0, 0, 0.1);
  padding: 0.4rem;
`;

const StyledButton = styled.button`
  border: none;
  background-color: transparent;
  border-radius: 4px;
  color: ${(props) => props.theme.textColor || '#333'};
  padding: 6px;
  font-size: 14px;

  &:hover {
    background-color: ${(props) => props.theme.secondaryColor || '#f7f7f7'};
    cursor: pointer;
  }
  &.is-active {
    background-color: ${(props) => props.theme.primaryColor || '#dcdcdc'};
    color: ${(props) => props.theme.backgroundColor || '#333'};
  }
  &:disabled {
    opacity: 0.4;
    cursor: not-allowed;
  }
`;

function CompactBubbleMenuToolbar({ editor, hideMenu, theme }) {
  const [colorPickerOpen, setColorPickerOpen] = useState(false);
  const [headingDropdownOpen, setHeadingDropdownOpen] = useState(false);
  const [selectedColor, setSelectedColor] = useState('#115DF1');

  const editorState = useEditorState({
    editor,
    selector: ({ editor: e }) => ({
      isBold: e.isActive('bold'),
      isItalic: e.isActive('italic'),
      isStrike: e.isActive('strike'),
      isUnderline: e.isActive('underline'),
      isHeading: e.isActive('heading'),
      h1: e.isActive('heading', { level: 1 }),
      h2: e.isActive('heading', { level: 2 }),
      h3: e.isActive('heading', { level: 3 }),
      h4: e.isActive('heading', { level: 4 }),
      h5: e.isActive('heading', { level: 5 }),
      h6: e.isActive('heading', { level: 6 }),
    }),
  });

  if (hideMenu) return null;

  const toggleColorPicker = () => {
    setColorPickerOpen(!colorPickerOpen);
    editor.chain().focus().run();
  };

  return (
    <BubbleMenuContainer theme={theme}>
      <StyledButton
        theme={theme}
        onClick={() => editor.chain().focus().toggleBold().run()}
        className={editorState.isBold ? 'is-active' : ''}
      >
        <Icon icon={faBold} />
      </StyledButton>
      <StyledButton
        theme={theme}
        onClick={() => editor.chain().focus().toggleItalic().run()}
        className={editorState.isItalic ? 'is-active' : ''}
      >
        <Icon icon={faItalic} />
      </StyledButton>
      <StyledButton
        theme={theme}
        onClick={() => editor.chain().focus().toggleStrike().run()}
        className={editorState.isStrike ? 'is-active' : ''}
      >
        <Icon icon={faStrikethrough} />
      </StyledButton>
      <StyledButton
        theme={theme}
        onClick={() => editor.chain().focus().toggleUnderline().run()}
        className={editorState.isUnderline ? 'is-active' : ''}
      >
        <Icon icon={faUnderline} />
      </StyledButton>
      <StyledButton
        theme={theme}
        onClick={() => {
          setHeadingDropdownOpen(!headingDropdownOpen);
          editor.chain().focus().run();
        }}
        className={editorState.isHeading ? 'is-active' : ''}
        style={{ position: 'relative' }}
      >
        <Icon icon={faHeading} />
      </StyledButton>
      {headingDropdownOpen && (
      <HeadingDropdown theme={theme}>
        {[1, 2, 3, 4, 5, 6].map((lvl) => (
          <button
            key={`heading-${lvl}`}
            onClick={() => {
              editor.chain().focus().toggleHeading({ level: lvl }).run();
              setHeadingDropdownOpen(false);
            }}
            className={editorState[`h${lvl}`] ? 'is-active' : ''}
          >
            H
            {lvl}
          </button>
        ))}
      </HeadingDropdown>
      )}
      <StyledButton
        theme={theme}
        onClick={() => editor.chain().focus().unsetAllMarks().run()}
      >
        <Icon icon={faRemoveFormat} />
      </StyledButton>
      <StyledButton
        theme={theme}
        onClick={toggleColorPicker}
        className={
          editor.isActive('textStyle', { color: selectedColor })
            ? 'is-active'
            : ''
        }
      >
        <Icon icon={faHighlighter} />
      </StyledButton>

      {colorPickerOpen && (
      <ColorPickerPopover theme={theme}>
        <HexAlphaColorPicker
          color={selectedColor}
          onChange={(val) => {
            setSelectedColor(val);
            editor.chain().focus().setColor(val).run();
          }}
        />
      </ColorPickerPopover>
      )}
    </BubbleMenuContainer>
  );
}

function TiptapCompactEditor({
  content,
  disabled,
  onUpdate,
  onFocus,
  onBlur,
  hideMenu,
  autoFocus,

  borderColor,
  borderRadius,
  borderWidth,
  backgroundColor,
  padding,

  fontSize,
  fontStyle,
  fontWeight,
  textAlign,
  textTransform,
  textDecoration,
  textOverflow,
  lineHeight
}) {
  const theme = useContext(ThemeContext);

  const onUpdateRef = React.useRef(onUpdate);
  const onFocusRef = React.useRef(onFocus);
  const onBlurRef = React.useRef(onBlur);
  onUpdateRef.current = onUpdate;
  onFocusRef.current = onFocus;
  onBlurRef.current = onBlur;

  const handleUpdate = useCallback(({ editor }) => {
    if (onUpdateRef.current) {
      onUpdateRef.current(editor.getJSON());
    }
  }, []);

  const handleFocus = useCallback(() => {
    if (onFocusRef.current) {
      onFocusRef.current();
    }
  }, []);

  const handleBlur = useCallback(({ editor }) => {
    if (onBlurRef.current) {
      onBlurRef.current(editor.getJSON());
    }
  }, []);

  const extensions = useMemo(() => [
    StarterKit.configure({
      bulletList: false,
      orderedList: false,
    }),
    TextStyle,
    Color,
    Underline,
  ], []);

  const editor = useEditor({
    content,
    editable: !disabled,
    extensions,
    immediatelyRender: true,
    shouldRerenderOnTransaction: false,
    autofocus: autoFocus || false,
    onUpdate: handleUpdate,
    onFocus: handleFocus,
    onBlur: handleBlur,
  });

  useEffect(() => {
    if (editor) {
      editor.setEditable(!disabled);
    }
  }, [editor, disabled]);

  if (!editor) return null;

  return (
    <CompactTiptapInput
      theme={theme}
      {...{
        borderColor,
        borderRadius,
        borderWidth,
        backgroundColor,
        padding,
        fontSize,
        fontStyle,
        fontWeight,
        textAlign,
        textTransform,
        textDecoration,
        textOverflow,
        lineHeightPixels: lineHeight
      }}
    >
      <BubbleMenu editor={editor} tippyOptions={{ duration: 100, maxWidth: 384, zIndex: 52000, }}>
        <CompactBubbleMenuToolbar editor={editor} hideMenu={hideMenu} theme={theme} />
      </BubbleMenu>

      <EditorContent
        style={{ height: '100%' }}
        editor={editor}
      />
    </CompactTiptapInput>
  );
}

TiptapCompactEditor.propTypes = {
  content: PropTypes.oneOfType([PropTypes.object, PropTypes.string]),
  disabled: PropTypes.bool,
  onUpdate: PropTypes.func,
  onFocus: PropTypes.func,
  onBlur: PropTypes.func,
  hideMenu: PropTypes.bool,
  autoFocus: PropTypes.oneOfType([PropTypes.bool, PropTypes.string]),

  borderColor: PropTypes.string,
  borderRadius: PropTypes.number,
  borderWidth: PropTypes.number,
  backgroundColor: PropTypes.string,
  padding: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  fontSize: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  fontStyle: PropTypes.string,
  fontWeight: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  textAlign: PropTypes.string,
  textTransform: PropTypes.string,
  textDecoration: PropTypes.string,
  textOverflow: PropTypes.string,
  lineHeight: PropTypes.number,
};

export default memo(TiptapCompactEditor);
